import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { DatabaseReference, off, onValue, ref } from 'firebase/database';
import { forkJoin, map } from 'rxjs';
import { toFirebaseChannelConnection } from '../models/firebase-channel/firebase-channel.deserialize';
import {
    FirebaseChannelConnection,
    FirebaseChannelData,
} from '../models/firebase-channel/firebase-channel.model';
import { channelOnValue } from '../store/firebase-channel/firebase-channel.actions';
import { FirebaseTransportService } from './firebase-transport.service';

@Injectable({ providedIn: 'root' })
export class FirebaseChannelService {
    private readonly http = inject(HttpClient);
    private readonly transportService = inject(FirebaseTransportService);
    private readonly store = inject(Store);

    private readonly databaseReferences: Partial<
        Record<string, DatabaseReference>
    > = {};

    connect(channelUrl: string, name: string): void {
        console.log(`Trying to initialize Firebase ${name} channel...`);

        forkJoin([
            this.transportService.initializeChannelClientTransport(),
            this.http.get(channelUrl),
        ])
            .pipe(
                map(([clientTransport, channel]) =>
                    toFirebaseChannelConnection(channel, clientTransport),
                ),
            )
            .subscribe({
                next: (channelConnection) =>
                    this.channelOnChanges(name, channelConnection),
                error: (error) =>
                    console.error(
                        `Can't connect to Firebase ${name} channel.`,
                        error,
                    ),
            });
    }

    disconnect(name: string) {
        const channel = this.databaseReferences[name];
        if (channel) {
            console.debug(`Disconnect Firebase ${name} channel`);

            off(channel);
            delete this.databaseReferences[name];
        }
    }

    disconnectAll() {
        Object.keys(this.databaseReferences).forEach((name) =>
            this.disconnect(name),
        );
    }

    private channelOnChanges(
        name: string,
        channelConnection: FirebaseChannelConnection,
    ) {
        this.disconnect(name);

        const databaseReference = ref(
            channelConnection.clientTransport.firebaseDB,
            channelConnection.firebaseChannelPath,
        );

        console.log(`Firebase ${name} channel connected.`);

        this.databaseReferences[name] = databaseReference;

        onValue(
            databaseReference,
            (snapshot) => {
                const value: FirebaseChannelData = snapshot.val();
                this.store.dispatch(channelOnValue({ name, value }));
            },
            (error) =>
                console.error(`Firebase ${name} channel onValue error`, error),
        );
    }
}
